// Ask flow for AgentPanel (investigating-analyst Task 15).
//
// Pure of React and of the network: every function takes its collaborators, so the
// decisions the panel's effect used to hide (inline vs 202, the poll, the live trail, job
// expiry, cancel, retry, legacy storage) are testable with plain-function fakes. Lives in
// utils/ because a .jsx component must not export logic (importing a view into Jest pulls
// react-router, frontend/CLAUDE.md).
//
// Server contract (proxy-server/conformance/askDelivery.js): POST /checks/agent/ask with
// async:true answers inline 200 (a free rung) or 202 { jobId }; GET .../jobs/:id returns the
// job record `{ status, result, error, trail }`, and `trail` grows while it runs.
//
// Never clears a thread: clearThread is for an explicit user action only and nothing here
// calls it. Nothing here reacts to a history_snapshot.

import {
  appendTurn, updateTurn, buildThreadContext, loadThread, newThread, threadKey,
} from './askThread';
import { normalizeAskResult } from './askShape';

export const LEGACY_PENDING_KEY = 'checks.askPending';
export const LEGACY_THREAD_KEY = 'checks.askThread';
// The poll is the recovery path and the live-trail feed; the WS push is the fast path.
export const ASK_POLL_MS = 2000;

export const ASK_FLOW_COPY = {
  expired: 'This investigation expired before its answer was collected.',
  cancelled: 'Stopped waiting. The server run may still finish and bill.',
  failed: 'The question failed.',
  noAnswer: 'The investigation ended without an answer.',
};

const IN_PROGRESS = ['running', 'queued', 'pending'];
const EXTRA_KEYS = ['via', 'plan', 'rowCount', 'scopedFrom', 'param', 'byHost', 'inconsistentHosts', 'modelNote'];
const EXTRA_LIST_MAX = 50;

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

export function errText(e) {
  return (e && e.body && e.body.error) || (e && e.message) || 'something went wrong';
}

/* ---- results and jobs -> turn patches ------------------------------------------------- */

/**
 * askJobToTurn(job) -> the raw result a finished job contributes, or null while running.
 * A failed or cancelled job still yields one: a question must not vanish. A cancelled job
 * can carry the answer it was already charged for (docs/checks-ask-async-spec.md).
 */
export function askJobToTurn(job) {
  if (!isObj(job)) return null;
  if (IN_PROGRESS.includes(job.status)) return null;
  if (isObj(job.result)) return job.result;
  return {
    ok: false,
    answer: '',
    error: job.error || (job.status === 'cancelled' ? 'cancelled' : ASK_FLOW_COPY.noAnswer),
  };
}

// The pre-investigation rungs (compare / params / prepared) return fields the structured
// card has no slot for. Kept in memory under `extra` for AgentPanel's table; askThread
// does not persist it.
function extraOf(src) {
  if (!isObj(src)) return undefined;
  const out = {};
  for (const k of EXTRA_KEYS) {
    if (src[k] === undefined || src[k] === null) continue;
    out[k] = Array.isArray(src[k]) ? src[k].slice(0, EXTRA_LIST_MAX) : src[k];
  }
  return Object.keys(out).length ? out : undefined;
}

function unwrap(raw) {
  if (!isObj(raw)) return raw;
  const looksLikeJob = typeof raw.jobId === 'string' || typeof raw.status === 'string';
  return looksLikeJob && typeof raw.answer !== 'string' && isObj(raw.result) ? raw.result : raw;
}

/** resultToPatch(raw) -> the updateTurn patch for ANY delivered shape. Never throws. */
export function resultToPatch(raw) {
  const v = normalizeAskResult(raw);
  const failed = !v.ok && !(v.degraded && v.answer);
  let status = 'done';
  if (failed) status = 'error';
  else if (v.capHit) status = 'capHit';
  else if (v.degraded) status = 'degraded';
  const patch = {
    status,
    answer: v.answer,
    markdown: v.markdown,
    evidence: v.evidence,
    next: v.next,
    followUps: v.followUps,
    trail: v.trail,
    partial: v.partial,
    degraded: v.degraded,
    completedAt: Date.now(),
  };
  if (v.usage) patch.usage = v.usage;
  if (v.scope) patch.scope = v.scope;
  if (failed) patch.error = v.error || ASK_FLOW_COPY.failed;
  const extra = extraOf(unwrap(raw));
  if (extra) patch.extra = extra;
  return patch;
}

/** jobToPatch(job) -> patch for a finished job, null while it is still running. */
export function jobToPatch(job) {
  const out = askJobToTurn(job);
  return out ? resultToPatch(out) : null;
}

/** The live trail on a (running) job record, normalised and capped like a result's. */
export function jobTrail(job) {
  if (!isObj(job) || !Array.isArray(job.trail)) return [];
  return normalizeAskResult({ evidence: [], trail: job.trail }).trail;
}

/* ---- evidence jump -------------------------------------------------------------------- */

/**
 * resolveRowRef(rowRef, rowIds, requestId) -> requestId | null.
 * The server stamps `requestId` (the row's own stable id) next to `rowRef` on every evidence item whose row has one;
 * when present it wins and no mapping is needed (it works for whole-session questions). Otherwise:
 * `rowRef` is an index into the rows the SERVER resolved the question over. The client can
 * only name that row when it POSTED the rows itself (a Dashboard selection), so `rowIds` is
 * the requestId list of the posted rows, snapshotted at ask time. A whole-session question
 * posts no rows, the server's order is unknowable here, and the answer is null: a wrong jump
 * is worse than none.
 */
export function resolveRowRef(rowRef, rowIds, requestId) {
  if (typeof requestId === 'string' && requestId.trim()) return requestId;
  if (!Array.isArray(rowIds)) return null;
  const idx = Number.isInteger(rowRef) ? rowRef
    : (isObj(rowRef) && Number.isInteger(rowRef.rowIndex) ? rowRef.rowIndex : null);
  if (idx === null || idx < 0 || idx >= rowIds.length) return null;
  const id = rowIds[idx];
  return typeof id === 'string' && id ? id : null;
}

/* ---- storage -------------------------------------------------------------------------- */

/** The old in-flight question (`checks.askPending` in sessionStorage), or null. */
export function loadLegacyPending(storage) {
  try {
    const parsed = JSON.parse(storage.getItem(LEGACY_PENDING_KEY) || 'null');
    if (!isObj(parsed) || typeof parsed.jobId !== 'string' || !parsed.jobId) return null;
    return { jobId: parsed.jobId, q: typeof parsed.q === 'string' ? parsed.q : '' };
  } catch { return null; }
}

/** Turn a legacy pending question into a working turn (unless the thread has that job). */
export function adoptLegacyPending(thread, storage) {
  const pending = loadLegacyPending(storage);
  try { if (storage && typeof storage.removeItem === 'function') storage.removeItem(LEGACY_PENDING_KEY); } catch { /* nothing */ }
  if (!pending) return thread;
  if ((thread.turns || []).some((t) => t.jobId === pending.jobId)) return thread;
  const r = appendTurn(thread, pending.q || '(question lost in reload)');
  return updateTurn(r.thread, r.turnId, { jobId: pending.jobId });
}

/**
 * loadInitialThread({ local, session, sessionId }) -> thread.
 * The per-session key in localStorage wins. When it is empty, the old sessionStorage
 * `{ q, out }` thread is read (askThread's legacy tolerance) and then dropped, and the old
 * pending job is adopted. Never throws.
 */
export function loadInitialThread({ local, session, sessionId } = {}) {
  let thread = loadThread(local, threadKey(sessionId));
  try {
    if (!thread.turns.length) {
      const legacy = loadThread(session, LEGACY_THREAD_KEY);
      if (legacy.turns.length) thread = { threadId: thread.threadId, turns: legacy.turns };
    }
    if (session && typeof session.removeItem === 'function') session.removeItem(LEGACY_THREAD_KEY);
    thread = adoptLegacyPending(thread, session);
  } catch { /* the thread we have is fine */ }
  return thread || newThread();
}

/* ---- the runner ----------------------------------------------------------------------- */

/**
 * createAskRunner({ store, agentAsk, getAskJob, sleep, pollMs })
 *   store: { get(): thread, set(thread) }   (the panel backs it with a ref + setState)
 *   agentAsk(body) / getAskJob(jobId) -> api/checks; sleep(ms) -> Promise.
 * Returns { ask, resume, settleJob, cancel, dispose }.
 */
export function createAskRunner({ store, agentAsk, getAskJob, sleep, pollMs = ASK_POLL_MS }) {
  const following = new Set();
  const stopped = new Set();
  let disposed = false;

  const turnOf = (id) => store.get().turns.find((t) => t.id === id);
  const working = (id) => {
    const t = turnOf(id);
    return !disposed && !!t && t.status === 'working' && !stopped.has(id);
  };
  const patch = (id, p) => store.set(updateTurn(store.get(), id, p));

  // Land a finished patch. A live trail the final result lacks is kept, not wiped.
  function finish(turnId, p) {
    const cur = turnOf(turnId);
    const next = { ...p };
    if (cur && Array.isArray(cur.trail) && Array.isArray(next.trail) && next.trail.length < cur.trail.length) {
      delete next.trail;
    }
    patch(turnId, next);
  }

  async function follow(turnId, jobId, immediate) {
    if (following.has(jobId)) return;
    following.add(jobId);
    try {
      let first = !!immediate;
      while (working(turnId)) {
        if (!first) await sleep(pollMs);
        first = false;
        if (!working(turnId)) return;
        let job;
        try { job = await getAskJob(jobId); } catch { job = undefined; } // a failed poll is not a failed job
        if (!working(turnId)) return;
        if (job === null) {
          patch(turnId, { status: 'error', error: ASK_FLOW_COPY.expired, completedAt: Date.now() });
          return;
        }
        if (job) {
          const done = jobToPatch(job);
          if (done) { finish(turnId, done); return; }
          const trail = jobTrail(job);
          const cur = turnOf(turnId);
          if (cur && trail.length > (cur.trail || []).length) patch(turnId, { trail });
        }
      }
    } finally {
      following.delete(jobId);
    }
  }

  /** ask(question, ctx, opts) -> turnId | null (null: blank, or a question already running). */
  async function ask(question, ctx, opts) {
    const q = typeof question === 'string' ? question.trim() : '';
    if (!q) return null;
    if (store.get().turns.some((t) => t.status === 'working' && !stopped.has(t.id))) return null;
    const context = buildThreadContext(store.get());
    const r = appendTurn(store.get(), q);
    store.set(r.thread);
    const turnId = r.turnId;
    if (opts && Array.isArray(opts.rowIds)) patch(turnId, { rowIds: opts.rowIds });
    try {
      const out = await agentAsk({ ...(ctx || {}), text: q, thread: context, async: true });
      if (!working(turnId)) return turnId;
      if (isObj(out) && typeof out.jobId === 'string' && out.jobId) {
        patch(turnId, { jobId: out.jobId });
        await follow(turnId, out.jobId, false);
      } else {
        finish(turnId, resultToPatch(out));
      }
    } catch (e) {
      if (working(turnId)) patch(turnId, { status: 'error', error: errText(e), completedAt: Date.now() });
    }
    return turnId;
  }

  /** Re-attach to every working turn that has a job (after a reload or a remount). */
  function resume() {
    const jobs = store.get().turns.filter((t) => t.status === 'working' && t.jobId);
    return Promise.all(jobs.map((t) => follow(t.id, t.jobId, true)));
  }

  /** WS push: settle a working turn from a finished job. Idempotent, ignores running. */
  function settleJob(turnId, job) {
    if (!working(turnId)) return false;
    const done = jobToPatch(job);
    if (!done) return false;
    finish(turnId, done);
    return true;
  }

  /** Stop waiting (AC 80). The server run is not aborted and may still bill. */
  function cancel() {
    for (const t of store.get().turns) {
      if (t.status !== 'working') continue;
      stopped.add(t.id);
      patch(t.id, { status: 'error', error: ASK_FLOW_COPY.cancelled, completedAt: Date.now() });
    }
  }

  function dispose() { disposed = true; }

  return { ask, resume, settleJob, cancel, dispose };
}
